/**
 * Shared admin status vocabulary. Maps raw `jobs.status` (and a few other
 * record statuses) to a calm tone + human label so chips, calendar blocks and
 * legends stay consistent across admin pages.
 */
export type AdminTone = 'green' | 'blue' | 'violet' | 'amber' | 'rose' | 'mint' | 'grey';

export interface StatusMeta {
  label: string;
  tone: AdminTone;
}

export const JOB_STATUS_META: Record<string, StatusMeta> = {
  pending_confirmation: { label: 'Pending', tone: 'violet' },
  // Pay to book: a card booking holding its slot while the customer pays.
  awaiting_payment: { label: 'Awaiting payment', tone: 'amber' },
  confirmed: { label: 'Confirmed', tone: 'mint' },
  scheduled: { label: 'Scheduled', tone: 'blue' },
  in_progress: { label: 'In progress', tone: 'amber' },
  completed: { label: 'Completed', tone: 'green' },
  cancelled: { label: 'Cancelled', tone: 'rose' },
  // Common non-job statuses so other admin pages can reuse StatusChip.
  pending: { label: 'Pending', tone: 'amber' },
  contacted: { label: 'Contacted', tone: 'blue' },
  converted: { label: 'Converted', tone: 'green' },
  quoted: { label: 'Quoted', tone: 'violet' },
  accepted: { label: 'Accepted', tone: 'mint' },
  declined: { label: 'Declined', tone: 'rose' },
  partially_paid: { label: 'Partially paid', tone: 'amber' },
  active: { label: 'Active', tone: 'green' },
  inactive: { label: 'Inactive', tone: 'grey' },
  paid: { label: 'Paid', tone: 'green' },
  sent: { label: 'Sent', tone: 'blue' },
  draft: { label: 'Draft', tone: 'grey' },
  viewed: { label: 'Viewed', tone: 'violet' },
  expired: { label: 'Expired', tone: 'grey' },
  overdue: { label: 'Overdue', tone: 'rose' },
  refunded: { label: 'Refunded', tone: 'grey' },
  void: { label: 'Void', tone: 'grey' },
  unassigned: { label: 'Unassigned', tone: 'grey' },
};

export function getStatusMeta(status: string | null | undefined): StatusMeta {
  if (!status) return { label: 'Unknown', tone: 'grey' };
  return (
    JOB_STATUS_META[status] ?? {
      label: status.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
      tone: 'grey',
    }
  );
}

/** Stable, pleasant hue for a person (avatars, calendar column accents). */
const PERSON_HUES = [263, 172, 214, 340, 32, 196, 148, 12];

export function personHue(seed: string | null | undefined): number {
  if (!seed) return 228;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return PERSON_HUES[hash % PERSON_HUES.length];
}

export function initialsOf(name: string | null | undefined): string {
  if (!name) return '?';
  return (
    name
      .trim()
      .split(/\s+/)
      .filter((part) => /^[\p{L}\p{N}]/u.test(part))
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '?'
  );
}
